import { z } from "zod";
import type { Operation } from "./deltas";
import { filterMatches } from "./filters";
import { numberAt, type TimeMap } from "./reading";
import {
  type Animated,
  CameraSchema,
  type Change,
  isAround,
  isCover,
  isDrawnThing,
  isTrack,
  type Key,
  type Thing,
  type ThingKind,
  ThingSchema,
  type Track,
} from "./things";
import {
  type Clarification,
  type Dim,
  type Label,
  type Scene,
  SpotlightSchema,
  sceneSeconds,
} from "./timeline";

/**
 * What a map plays: things over `length` seconds, the camera's moves through them, and spotlights
 * on them. A scene becomes one, with the world beneath it at the ticks it shows and its own things
 * in its seconds.
 */
export const PlaybackSchema = z.strictObject({
  length: z.number().nonnegative(),
  camera: CameraSchema.optional(),
  things: z.array(ThingSchema),
  spotlights: z.array(SpotlightSchema).optional(),
  /** Changes to things already on the map, by id: each property given replaces the thing's own. */
  changes: z.array(z.looseObject({ id: z.string() })).optional(),
});
export type Playback = Omit<z.infer<typeof PlaybackSchema>, "changes"> & { changes?: Change[] };

/** What a write puts on a map, or a playback plays over it. */
interface Written {
  things?: Thing[];
  changes?: Change[];
}

/** The most of a map one starting clarification holds as JSON, well under the most a delta may. */
const STARTING_BYTES = 800_000;

/** Kinds in the order a starting map places them: categories and canvases, then what is on them. */
const KIND_ORDER: ThingKind[] = [
  "category",
  "canvas",
  "shape",
  "line",
  "point",
  "media",
  "particles",
  "text",
  "card",
  "entry",
  "link",
];

/**
 * The ids a thing refers to: a canvas's category; the canvas anything else is on; the point a line
 * goes round; the points a cover clears round; the point or line a text is on or along; the point
 * a card stands on; each entry an entry sits under in a tree; and the two entries a link joins.
 */
function referencesOf(thing: Thing): string[] {
  switch (thing.kind) {
    case "category":
      return [];
    case "canvas":
      return thing.category === undefined ? [] : [thing.category];
    case "line":
      return isAround(thing.geometry)
        ? [thing.canvas, thing.geometry.around.point]
        : [thing.canvas];
    case "shape":
      return isCover(thing.fill) && thing.fill.clears
        ? [thing.canvas, ...thing.fill.clears.map(clear => clear.point)]
        : [thing.canvas];
    case "text":
    case "card": {
      const { on } = thing;
      if ("point" in on) return [thing.canvas, on.point];
      if ("line" in on) return [thing.canvas, on.line];
      if ("along" in on && typeof on.along === "string") return [thing.canvas, on.along];
      return [thing.canvas];
    }
    case "entry": {
      const places = isTrack(thing.place) ? thing.place.keys.map(key => key.value) : [thing.place];
      return [thing.canvas, ...places.filter(place => typeof place === "string")];
    }
    case "link":
      return [thing.canvas, ...thing.ends];
    default:
      return [thing.canvas];
  }
}

/**
 * A map in the order a starting map places it: kind by kind, in the map's own order within each,
 * and each thing after every thing on the map it refers to. One that would come before a thing it
 * refers to, as a cover comes before the points it clears round, waits for the last of them.
 * Entries that sit under one another in a ring, which no order can place, come last, with
 * whatever refers to them.
 */
function inPlacingOrder(map: readonly Thing[]): Thing[] {
  const ofKind = new Map<ThingKind, Thing[]>(KIND_ORDER.map(kind => [kind, []]));
  for (const thing of map) ofKind.get(thing.kind)?.push(thing);
  const inKindOrder = KIND_ORDER.flatMap(kind => ofKind.get(kind) ?? []);
  const onMap = new Set(map.map(thing => thing.id));
  const placed = new Set<string>();
  /** Things waiting to be placed, by the id of an unplaced thing on the map each refers to. */
  const waiting = new Map<string, Thing[]>();
  const order: Thing[] = [];
  const place = (thing: Thing) => {
    const unplaced = referencesOf(thing).find(
      id => id !== thing.id && onMap.has(id) && !placed.has(id),
    );
    if (unplaced !== undefined) {
      const others = waiting.get(unplaced);
      if (others) others.push(thing);
      else waiting.set(unplaced, [thing]);
      return;
    }
    order.push(thing);
    placed.add(thing.id);
    const released = waiting.get(thing.id) ?? [];
    waiting.delete(thing.id);
    for (const other of released) place(other);
  };
  for (const thing of inKindOrder) place(thing);
  if (order.length === inKindOrder.length) return order;
  const ordered = new Set(order);
  return [...order, ...inKindOrder.filter(thing => !ordered.has(thing))];
}

const utf8 = new TextEncoder();

/**
 * A map as the clarifications that lay a world out at tick 0 before it starts, the first labelling
 * tick 0 `date`, each small enough to be a delta: the map in the order it is placed, and a new
 * clarification wherever the next thing would take one past `STARTING_BYTES` as JSON, so whatever
 * a thing refers to is in its clarification or an earlier one. `world.started` follows them.
 */
export function startingClarifications(map: readonly Thing[], date: string): Clarification[] {
  const parts: Thing[][] = [];
  let part: Thing[] = [];
  let bytes = 0;
  for (const thing of inPlacingOrder(map)) {
    const size = utf8.encode(JSON.stringify(thing)).length;
    if (part.length > 0 && bytes + size > STARTING_BYTES) {
      parts.push(part);
      part = [];
      bytes = 0;
    }
    part.push(thing);
    bytes += size;
  }
  if (part.length > 0) parts.push(part);
  if (parts.length === 0) return [{ labels: [{ tick: 0, date }] }];
  return parts.map((things, index) => ({
    ...(index === 0 ? { labels: [{ tick: 0, date }] } : {}),
    things,
  }));
}

/** The changes something written makes, by the id each changes, each thing's in the order they take effect. */
function changesById(written: Written, start = 0): Map<string, Change[]> {
  const out = new Map<string, Change[]>();
  for (const change of written.changes ?? []) {
    out.set(change.id, [...(out.get(change.id) ?? []), change]);
  }
  for (const own of out.values()) own.sort((a, b) => (a.at ?? start) - (b.at ?? start));
  return out;
}

/** The map while something written plays: the map's things with its changes made, then its own. */
export function during(map: readonly Thing[], written: Written): Thing[] {
  const changes = changesById(written);
  return [
    ...map.map(thing => {
      const own = changes.get(thing.id);
      if (!own) return thing;
      const out: Record<string, unknown> = { ...thing };
      for (const { at: _at, ...change } of own) Object.assign(out, change);
      return out as Thing;
    }),
    ...(written.things ?? []),
  ];
}

/**
 * The id a thing of the timeline answers to: its own, or, for a thing as it was up to a change that
 * split it, the id of the thing it was.
 */
export const answersTo = (thing: Thing): string =>
  (thing as { partOf?: string }).partOf ?? thing.id;

/**
 * The map once something written has played: every thing still on it, holding the values its
 * tracks end on. A thing with an `until` leaves, and so does every thing on a canvas that leaves.
 */
export function after(map: readonly Thing[], written: Written): Thing[] {
  const leaves = (thing: Thing) => thing.until !== undefined;
  const played = during(map, written);
  const canvasesLeaving = new Set(
    played.filter(thing => thing.kind === "canvas" && leaves(thing)).map(thing => thing.id),
  );
  return played
    .filter(thing => !leaves(thing) && !("canvas" in thing && canvasesLeaving.has(thing.canvas)))
    .map(settled);
}

/** A thing holding what its tracks end on: the thing itself when nothing in it moves. */
function settled(thing: Thing): Thing {
  const timed = thing.from !== undefined || thing.until !== undefined;
  const { from: _from, until: _until, ...rest } = thing;
  const kept = heldEntries(rest);
  return kept || timed ? ((kept ?? rest) as unknown as Thing) : thing;
}

function held(value: unknown): unknown {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return value;
  if (isTrack(value as Animated<unknown>)) {
    const { keys } = value as Track<unknown>;
    return keys[keys.length - 1].value;
  }
  return heldEntries(value as Record<string, unknown>) ?? value;
}

/** An object holding what its tracks end on, or null when none of it moves. */
function heldEntries(value: Record<string, unknown>): Record<string, unknown> | null {
  let moved = false;
  const entries = Object.entries(value).map(([key, inner]) => {
    const kept = held(inner);
    if (kept !== inner) moved = true;
    return [key, kept] as const;
  });
  return moved ? Object.fromEntries(entries) : null;
}

/* ── The timeline ───────────────────────────────────────────────────── */

/** A view's history as its writes have written it. */
export interface Timeline {
  /** Every thing that has been on the map, as it was at every tick: its tracks keyed in ticks. */
  things: Thing[];
  /** Its labels, in order of tick, one at most at each. */
  labels: Label[];
  /** The tick it ends at. */
  end: number;
}

export const emptyTimeline = (): Timeline => ({ things: [], labels: [], end: 0 });

/**
 * The timeline once an operation is written onto it: continued by an extension, or clarified where
 * it ends. Any other operation leaves it as it is.
 */
export function writeTimeline(timeline: Timeline, operation: Operation): Timeline {
  if (operation.type === "timeline.extended") {
    const { extension } = operation;
    return {
      things: writeThings(timeline.things, extension, timeline.end),
      labels: withLabels(timeline.labels, extension.labels),
      end: extension.end,
    };
  }
  if (operation.type === "timeline.clarified") {
    const { clarification } = operation;
    return {
      things: writeThings(timeline.things, clarification, timeline.end),
      labels: withLabels(timeline.labels, clarification.labels),
      end: timeline.end,
    };
  }
  return timeline;
}

/** Labels with more written: each at a tick that has one replaces it. */
function withLabels(labels: readonly Label[], more: readonly Label[] | undefined): Label[] {
  if (!more?.length) return labels as Label[];
  const byTick = new Map(labels.map(label => [label.tick, label]));
  for (const label of more) byTick.set(label.tick, label);
  return [...byTick.values()].sort((a, b) => a.tick - b.tick);
}

/** The date labelled at exactly `tick`, or null where nothing is. */
export function labelAt(labels: readonly Label[], tick: number): string | null {
  return labels.find(label => Math.abs(label.tick - tick) < 1e-9)?.date ?? null;
}

/** The map where the timeline ends: what is on it then, as it is then. */
export const mapAtEnd = (timeline: Timeline): Thing[] => after(timeline.things, { things: [] });

/** The properties of each kind that move over time, as paths into the thing. */
const MOVING: Record<ThingKind, string[]> = {
  canvas: [],
  point: [
    "opacity",
    "position",
    "symbol.frame.fill",
    "symbol.glyph.text",
    "symbol.color",
    "size",
    "rotation",
    "glow.color",
  ],
  line: [
    "opacity",
    "geometry",
    "geometry.around.km",
    "drawn",
    "stroke.color",
    "stroke.dash.offset",
  ],
  shape: ["opacity", "geometry", "fill", "border.color"],
  text: ["opacity", "text", "on.at", "on.along.path", "on.across.path", "reveal.shown"],
  card: ["opacity", "on.at", "text"],
  media: ["opacity", "box", "video.speed"],
  particles: ["opacity", "density", "color"],
  entry: ["place", "title", "words", "value", "state", "progress"],
  link: [],
  category: [],
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The timeline's things with a write written onto them from `start`: things it places appear there,
 * and each change, to a thing already on the timeline or one the write places, takes effect at its
 * `at`, or at `start`. What moves keeps what came before and steps or runs on from there; a change
 * to anything else splits the thing there, the thing as it was kept up to the change and answering
 * to the same id, so the timeline can still show any earlier stretch as it was.
 */
export function writeThings(timeline: readonly Thing[], written: Written, start: number): Thing[] {
  const changes = changesById(written, start);
  const placed = (written.things ?? []).map(thing =>
    thing.from === undefined && start > 0 ? ({ ...thing, from: start } as Thing) : thing,
  );
  const placedIds = new Set(placed.map(thing => thing.id));
  const changed = (thing: Thing) => withChanges(thing, changes.get(thing.id) ?? [], start);
  return [
    ...timeline.flatMap(thing =>
      placedIds.has(thing.id) ? [partUpTo(thing, start)] : changed(thing),
    ),
    ...placed.flatMap(changed),
  ];
}

/** A thing with each of its changes made from its `at`, as the parts a change splitting it leaves. */
function withChanges(thing: Thing, changes: readonly Change[], start: number): Thing[] {
  const parts: Thing[] = [];
  let current = thing;
  for (const change of changes) {
    const [before, after] = changedAt(current, change, change.at ?? start);
    if (before) parts.push(before);
    current = after;
  }
  return [...parts, current];
}

/**
 * A thing as it was up to `at`, under an id of its own, answering to the thing's. It goes as its
 * `exit` says only where the thing leaves by then: at `at`, it goes on as what comes after.
 */
function partUpTo(thing: Thing, at: number): Thing {
  const { exit: _exit, ...going } = thing as Thing & { exit?: unknown };
  const leaves = thing.until !== undefined && thing.until <= at;
  return Object.assign(
    {
      ...(leaves ? thing : going),
      id: `${thing.id}~${at}`,
      until: Math.min(thing.until ?? at, at),
    },
    { partOf: answersTo(thing) },
  ) as Thing;
}

/**
 * A thing with a change made from `at`: what moves keeps what came before and steps or runs on
 * from there, and when anything else given differs, the thing as it was up to `at` comes first and
 * what comes after does not enter. When it leaves and how, its `until` and `exit`, never split it.
 */
function changedAt(thing: Thing, change: Change, at: number): [Thing | null, Thing] {
  const { id: _id, at: _at, ...given } = change as Change & Record<string, unknown>;
  const moving = MOVING[thing.kind];
  const out: Record<string, unknown> = { ...thing };
  let switched = false;
  for (const [key, value] of Object.entries(given)) {
    if (key === "until" || key === "exit") {
      out[key] = value;
      continue;
    }
    const before = (thing as Record<string, unknown>)[key];
    const [next, differs] = continuedAt(key, before, value, at, moving);
    out[key] = next;
    if (differs) switched = true;
  }
  const changed = out as Thing;
  if (!switched || (thing.from ?? 0) >= at) return [null, changed];
  const { enter: _enter, ...after } = changed as Thing & { enter?: unknown };
  return [partUpTo(thing, at), { ...after, from: at } as Thing];
}

/** A property a change gives, continued from what it was, and whether anything that doesn't move differs. */
function continuedAt(
  path: string,
  before: unknown,
  next: unknown,
  at: number,
  moving: string[],
): [unknown, boolean] {
  if (next === undefined) return [next, before !== undefined];
  if (before === undefined) return [next, true];
  if (moving.includes(path) && continuable(before) && continuable(next)) {
    return [continued(before, next, at), false];
  }
  const within = moving.some(each => each.startsWith(`${path}.`));
  if (within && isObject(before) && isObject(next) && !isTrack(before) && !isTrack(next)) {
    const out: Record<string, unknown> = { ...next };
    let differs = Object.keys(before).some(key => !(key in next));
    for (const key of Object.keys(next)) {
      const [value, changed] = continuedAt(`${path}.${key}`, before[key], next[key], at, moving);
      out[key] = value;
      if (changed) differs = true;
    }
    return [out, differs];
  }
  return [next, !sameValue(before, next)];
}

/** Whether a value can run on into keys: a track, or one held value, but not a line along edges. */
const continuable = (value: unknown) => !isObject(value) || isTrack(value as Animated<unknown>);

/**
 * A value that is `before` up to `start` and `next` from there, keeping both: keys of `next` after
 * `start` run there from what `before` is at `start`.
 */
function continued<Value>(
  before: Animated<Value>,
  next: Animated<Value>,
  start: number,
): Animated<Value> {
  if (!isTrack(before) && !isTrack(next) && sameValue(before, next)) return next;
  const earlier: (Key<Value> & { ease?: string })[] = isTrack(before)
    ? before.keys.map(key => ({ ...key, ease: (key as { ease?: string }).ease ?? before.ease }))
    : [{ at: start, value: before }];
  const later = isTrack(next) ? next.keys : [{ at: start, value: next }];
  const last = earlier[earlier.length - 1] as Key<Value>;
  const keys = [
    ...earlier,
    ...(last.at < start ? [{ at: start, value: last.value }] : []),
    ...later,
  ];
  return { keys, ease: isTrack(next) ? next.ease : "step" } as Track<Value>;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/* ── Scenes ─────────────────────────────────────────────────────────── */

/**
 * The tick a scene has on screen at each of its seconds. A key earlier than the one before is cut
 * to as soon as that one is passed, so the map never runs backwards.
 */
export function sceneTick(scene: Pick<Scene, "tick">): TimeMap {
  const { tick } = scene;
  if (!isTrack(tick)) return () => tick;
  const runs: Track<number>[] = [];
  let keys: Key<number>[] = [];
  for (const key of tick.keys) {
    const last = keys.at(-1);
    if (last && key.value < last.value) {
      runs.push({ ...tick, keys });
      keys = [{ at: last.at, value: key.value }];
    }
    keys.push(key);
  }
  runs.push({ ...tick, keys });
  return t => {
    let run = runs[0] as Track<number>;
    for (let i = 1; i < runs.length; i++) {
      const next = runs[i] as Track<number>;
      if (t > (next.keys[0] as Key<number>).at) run = next;
    }
    return numberAt(run, t, 0);
  };
}

/**
 * A scene as a map plays it: the timeline beneath it at the tick its `tick` says, turned down as
 * its dims say, then its own things, its camera and its spotlights. Its own things leave as it
 * ends, whatever their `until`.
 */
export function scenePlayback(timeline: readonly Thing[], scene: Scene): Playback {
  const at = sceneTick(scene);
  const beneath = withDims(
    timeline.map(thing => thingOnClock(thing, at)),
    scene.dims ?? [],
  );
  return {
    length: sceneSeconds(scene),
    ...(scene.camera && { camera: scene.camera }),
    things: [...beneath, ...(scene.things ?? [])],
    ...(scene.spotlights && { spotlights: scene.spotlights }),
  };
}

/**
 * The timeline played over `length` seconds, the tick on screen at each as `ticks` says: what the
 * map shows when the timeline itself plays, or with one tick held, when it is scrubbed.
 */
export function timelinePlayback(
  timeline: readonly Thing[],
  ticks: Animated<number>,
  length = 0,
): Playback {
  const at: TimeMap = t => numberAt(ticks, t, 0);
  return { length, things: timeline.map(thing => thingOnClock(thing, at)) };
}

/** Things found to hold nothing on a clock, which every scene shows as they are. */
const timeless = new WeakSet<object>();

/** A thing on a scene's clock: itself when nothing in it is timed, which is found once. */
function thingOnClock(thing: Thing, map: TimeMap): Thing {
  if (timeless.has(thing)) return thing;
  const out = onClock(thing, map);
  if (out === thing) timeless.add(thing);
  return out;
}

/** A value with every track in it read through `map`, and a timed thing carrying `map` itself. */
function onClock<Value>(value: Value, map: TimeMap): Value {
  if (!isObject(value)) return value;
  if (isTrack(value as Animated<unknown>)) return { ...value, timeMap: map };
  let changed = value.from !== undefined || value.until !== undefined;
  const out: Record<string, unknown> = {};
  for (const [key, inner] of Object.entries(value)) {
    const mapped = isObject(inner) ? onClock(inner, map) : inner;
    if (mapped !== inner) changed = true;
    out[key] = mapped;
  }
  if (!changed) return value;
  if (value.from !== undefined || value.until !== undefined) out.timeMap = map;
  return out as Value;
}

/** How long a dim takes to come in before `from`, and to go after `until`. */
const DIM_FADE = 0.5;

/** A thing of the timeline as a scene's dims turn it down: each its share, in the scene's seconds. */
interface Dimmed {
  dims?: Track<number>[];
}

/** Things with the dims that name each, which the map multiplies into how strongly it shows. */
function withDims(things: Thing[], dims: readonly Dim[]): Thing[] {
  if (dims.length === 0) return things;
  const tracks = dims.map(dimTrack);
  return things.map(thing => {
    const named = { ...thing, id: answersTo(thing) };
    const own = tracks.filter((_, index) => {
      const { filter } = dims[index] as Dim;
      return !filter || filterMatches(filter, named);
    });
    if (own.length === 0 || !isDrawnThing(thing)) return thing;
    const dimmed: Thing & Dimmed = { ...thing, dims: own };
    return dimmed;
  });
}

/** A dim's share of how strongly things show at each second: 1 outside it, its `opacity` within. */
function dimTrack({ opacity, from, until }: Dim): Track<number> {
  return {
    keys: [
      { at: Math.max(0, from - DIM_FADE), value: 1 },
      { at: from, value: opacity },
      { at: until, value: opacity },
      { at: until + DIM_FADE, value: 1 },
    ],
    ease: "linear",
  };
}

/** How much of a thing shows at second `t` of a scene for the dims that turn it down: 1 for none. */
export function dimmedAt(thing: Thing, t: number): number {
  const { dims } = thing as Dimmed;
  if (!dims) return 1;
  let share = 1;
  for (const dim of dims) share *= numberAt(dim, t, 1);
  return share;
}
